import { z } from 'zod';
import { defineCallable } from './callable-contract';
import { CancelledReasonSchema, ConsultationOutcomeSchema } from './consultation';

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

const ConsultationTypeSchema = z.enum(['phone', 'video', 'in_person']);

const SuccessResultSchema = z.object({ success: z.boolean() });

// ---------------------------------------------------------------------------
// scheduleConsultation
// ---------------------------------------------------------------------------

export const ScheduleConsultationPayloadSchema = z.object({
  sessionId: z.string().min(1),
  organizationId: z.string().min(1),
  scheduledAt: z.iso.datetime(),
  consultationType: ConsultationTypeSchema.nullable(),
  videoLink: z.string().nullable(),
  notes: z.string().nullable(),
  assignedAttorney: z.string().nullable(),
  durationMinutes: z.number().int().min(5).max(480).default(30),
});
export type ScheduleConsultationPayload = z.infer<typeof ScheduleConsultationPayloadSchema>;

export const scheduleConsultationContract = defineCallable('scheduleConsultation', {
  input: ScheduleConsultationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// rescheduleConsultation
// ---------------------------------------------------------------------------

export const RescheduleConsultationPayloadSchema = z.object({
  sessionId: z.string().min(1),
  organizationId: z.string().min(1),
  scheduledAt: z.iso.datetime(),
  consultationType: ConsultationTypeSchema.nullable(),
  videoLink: z.string().nullable(),
  notes: z.string().nullable(),
  durationMinutes: z.number().int().min(5).max(480).nullish(),
  assignedAttorney: z.string().min(1).nullable().optional(),
});
export type RescheduleConsultationPayload = z.infer<typeof RescheduleConsultationPayloadSchema>;

export const rescheduleConsultationContract = defineCallable('rescheduleConsultation', {
  input: RescheduleConsultationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// cancelConsultation
// ---------------------------------------------------------------------------

export const CancelConsultationPayloadSchema = z
  .object({
    sessionId: z.string().min(1),
    organizationId: z.string().min(1),
    cancelledReason: CancelledReasonSchema,
    cancelledNote: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.cancelledReason === 'other' && !data.cancelledNote?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancelledNote'],
        message: 'cancelledNote is required when cancelledReason is "other"',
      });
    }
  });
export type CancelConsultationPayload = z.infer<typeof CancelConsultationPayloadSchema>;

export const cancelConsultationContract = defineCallable('cancelConsultation', {
  input: CancelConsultationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// dispositionConsultation
// ---------------------------------------------------------------------------

export const DispositionConsultationPayloadSchema = z.object({
  sessionId: z.string().min(1),
  organizationId: z.string().min(1),
  outcome: ConsultationOutcomeSchema,
  outcomeNotes: z.string().nullable(),
});
export type DispositionConsultationPayload = z.infer<typeof DispositionConsultationPayloadSchema>;

export const dispositionConsultationContract = defineCallable('dispositionConsultation', {
  input: DispositionConsultationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// revertDisposition
// ---------------------------------------------------------------------------

export const RevertDispositionPayloadSchema = z.object({
  sessionId: z.string().min(1),
  organizationId: z.string().min(1),
  consultationId: z.string().min(1),
});
export type RevertDispositionPayload = z.infer<typeof RevertDispositionPayloadSchema>;

export const revertDispositionContract = defineCallable('revertDisposition', {
  input: RevertDispositionPayloadSchema,
  output: SuccessResultSchema,
});
